import { useEffect, useMemo, useState } from 'react';

import {
  annotateDistrictMatches,
  fetchElectionBallot,
  fetchElectionDates,
} from '../utils/electionsApi';
import { fetchStateAuthority } from '../utils/localCandidatesApi';

/**
 * Upcoming elections for a state, and the ballot for one of them.
 *
 * Two requests in sequence, because the endpoints sit at different
 * granularities: /elections/dates answers when, and /elections/ballot answers
 * what is on a given date and who filed for it. The second needs an exact ISO
 * day, so it cannot run until the first has landed.
 *
 * They load independently on purpose. The date strip is the part that is always
 * available and is what a reader came for; making it wait on the ballot would
 * hold the whole panel behind the slower of two calls.
 *
 * Districts do not narrow either request. A date is set statewide, and
 * /elections/candidates has no chamber parameter, so a stored district cannot
 * filter without either hiding statewide races or confusing senate 16 with
 * house 16. They are used to mark rows instead -- see districtSlotFor in
 * utils/electionsApi. `inDistrictCount` is what a "your district" toggle should
 * be gated on: zero means nothing could be matched, which is a different thing
 * from the reader having no races.
 *
 * @param {Object}  params
 * @param {string}  params.stateAbbr - Two letter code. Required.
 * @param {Object}  [params.districts] - { congressional, upper, lower }, from
 *                                       districtsFromLocation(location).
 * @param {string}  [params.date] - Controlled: which date's ballot to load.
 *                                  Defaults to the next election. Owned by the
 *                                  caller so switching dates is a state change
 *                                  the page can see, rather than something
 *                                  hidden in here.
 * @param {string}  [params.electionType] - primary | general | runoff | special,
 *                                  when one date carries more than one.
 * @param {boolean} [params.includePast]
 * @param {boolean} [params.withBallot] - Set false for a dates-only strip.
 */
const useUpcomingElections = ({
  stateAbbr,
  districts,
  date = null,
  electionType = null,
  includePast = false,
  withBallot = true,
} = {}) => {
  const [elections, setElections] = useState([]);
  const [next, setNext] = useState(null);
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authority, setAuthority] = useState(null);

  const [contests, setContests] = useState([]);
  const [rawCandidates, setRawCandidates] = useState([]);
  const [ballotNotes, setBallotNotes] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [ballotLoading, setBallotLoading] = useState(false);
  const [ballotError, setBallotError] = useState(null);

  // Destructured to primitives before they reach a dependency array. The saved
  // location hands back a fresh object on every storage event, so depending on
  // `districts` itself would re-run on every unrelated write.
  const {
    congressional = null,
    upper = null,
    lower = null,
  } = districts || {};

  // ─── Dates ──────────────────────────────────────────────────────────────────
  useEffect(() => {

    if (!stateAbbr) {
      setElections([]);
      setNext(null);
      setNotes(null);
      setAuthority(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setAuthority(null);

        const result = await fetchElectionDates(stateAbbr, {
          includePast,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setElections(result.elections);
        setNext(result.next);
        setNotes(result.notes);

        // Every state should have a next election even in a quiet year -- the
        // November general is derived from statute -- so an empty list means
        // the question went unanswered rather than answered with "none". Point
        // at the state's own election office instead of closing it.
        if (!result.elections.length) {
          const fallback = await fetchStateAuthority(stateAbbr, {
            signal: controller.signal,
          });
          if (!controller.signal.aborted) setAuthority(fallback);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[useUpcomingElections] dates failed', {
          stateAbbr,
          status: err.status,
          query: err.query,
          message: err.message,
        });
        setElections([]);
        setNext(null);
        setNotes(null);
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [stateAbbr, includePast]);

  // The caller's choice wins; otherwise the soonest election that has not
  // happened. Kept as the ISO string rather than the election object so the
  // ballot effect below depends on a primitive.
  const selectedDate = date || next?.dateISO || null;

  // ─── Ballot ─────────────────────────────────────────────────────────────────
  useEffect(() => {

    if (!withBallot || !stateAbbr || !selectedDate) {
      setContests([]);
      setRawCandidates([]);
      setBallotNotes(null);
      setTruncated(false);
      setBallotError(null);
      setBallotLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setBallotLoading(true);
        setBallotError(null);

        const result = await fetchElectionBallot(
          { stateAbbr, date: selectedDate, electionType },
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;

        setContests(result.contests);
        setRawCandidates(result.candidates);
        setBallotNotes(result.notes);
        setTruncated(result.truncated);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // A failed ballot is a partial panel rather than a dead one: the dates
        // above it are already rendered and are still true. Logged rather than
        // swallowed, since the panel shows one sentence for every cause.
        console.error('[useUpcomingElections] ballot failed', {
          stateAbbr,
          date: selectedDate,
          electionType,
          status: err.status,
          query: err.query,
          message: err.message,
        });
        setContests([]);
        setRawCandidates([]);
        setBallotNotes(null);
        setTruncated(false);
        setBallotError(err);
      } finally {
        if (!controller.signal.aborted) setBallotLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [withBallot, stateAbbr, selectedDate, electionType]);

  // Annotation is a pure pass over rows already held, so it is a memo rather
  // than part of the fetch: changing a saved district re-marks the ballot
  // without asking for it again.
  const candidates = useMemo(
    () => annotateDistrictMatches(rawCandidates, { congressional, upper, lower }),
    [rawCandidates, congressional, upper, lower]
  );

  const inDistrictCount = useMemo(
    () => candidates.filter((candidate) => candidate.inDistrict).length,
    [candidates]
  );

  // The election object behind selectedDate, for a header that wants to name
  // what is being shown. Matched from the list rather than tracked separately,
  // so a caller-supplied date still resolves.
  const selected = useMemo(
    () => elections.find((election) => election.dateISO === selectedDate) || null,
    [elections, selectedDate]
  );

  return {
    // Dates
    elections,
    next,
    selected,
    selectedDate,
    // The cycle year of the selected election, for reuse as the `year` filter
    // on /elections/candidates so a county list shows this cycle rather than
    // everything on file.
    year: selected?.year || next?.year || null,
    notes,
    loading,
    error,
    authority,

    // Ballot
    contests,
    candidates,
    inDistrictCount,
    truncated,
    ballotNotes,
    ballotLoading,
    ballotError,
  };
};

export default useUpcomingElections;